import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { getArticles, createArticle, updateArticle, deleteArticle, getProjets, articleImageUrl } from '../services/api';
import { FiPlus, FiEdit2, FiTrash2, FiX, FiEye, FiUpload } from 'react-icons/fi';

const Articles = () => {
  const [articles, setArticles] = useState<any[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewArticle, setPreviewArticle] = useState<any>(null);
  const [editingId, setEditingId] = useState('');
  const [projets, setProjets] = useState<any[]>([]);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [form, setForm] = useState({ titre: '', projet_id: '', article: '' });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');
  const [saving, setSaving] = useState(false);

  const loadData = () => {
    getArticles().then(r => {
      setArticles(Array.isArray(r.data) ? r.data : r.data.data || []);
    }).catch(() => {});
  };

  useEffect(() => { loadData(); }, []);
  useEffect(() => { getProjets().then(r => setProjets(r.data)).catch(() => {}); }, []);

  const resetForm = () => {
    setForm({ titre: '', projet_id: '', article: '' });
    setImageFile(null);
    setImagePreview('');
  };

  const openAdd = () => { resetForm(); setEditingId(''); setShowModal(true); };
  const openEdit = (a: any) => {
    setForm({
      titre: a.titre || '',
      projet_id: a.projet_id || '',
      article: a.article || ''
    });
    setImageFile(null);
    setImagePreview(a.imageprincipale ? articleImageUrl(a.imageprincipale) : '');
    setEditingId(a.id);
    setShowModal(true);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (ev) => setImagePreview(ev.target?.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('titre', form.titre);
      fd.append('article', form.article);
      fd.append('projet_id', form.projet_id);
      if (imageFile) fd.append('image', imageFile);

      if (editingId) {
        await updateArticle(editingId, fd);
        setSuccess('Article mis à jour');
      } else {
        await createArticle(fd);
        setSuccess('Article créé avec succès');
      }
      setShowModal(false);
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors de l\'enregistrement');
      setTimeout(() => setError(''), 5000);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Supprimer cet article ?')) return;
    await deleteArticle(id);
    loadData();
  };

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'list': 'ordered' }, { 'list': 'bullet' }],
      [{ 'align': [] }],
      ['blockquote'],
      ['link', 'image'],
      ['clean']
    ]
  };

  return (
    <div>
      <h1 className="page-title">Gestion des articles</h1>
      <p style={{ color: '#888', fontSize: '0.9rem', marginTop: -8, marginBottom: 16 }}>
        Les articles publiés ici alimentent la section Actualités du site public.
      </p>

      {success && <div className="alert alert-success">{success}</div>}
      {error && <div className="alert alert-error">{error}</div>}

      <div className="card">
        <div className="card-header">
          <span>Articles ({articles.length})</span>
          <button className="btn btn-success" onClick={openAdd}><FiPlus /> Nouvel article</button>
        </div>
        <div className="card-body">
          <div className="table-responsive">
            <table className="data-table">
              <thead>
                <tr><th>Image</th><th>Date</th><th>Titre</th><th>Projet</th><th>Aperçu</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {articles.map(a => (
                  <tr key={a.id}>
                    <td style={{ width: 60 }}>
                      {a.imageprincipale ? (
                        <img src={articleImageUrl(a.imageprincipale)} alt="" style={{ width: 50, height: 36, objectFit: 'cover', borderRadius: 6 }} />
                      ) : (
                        <div style={{ width: 50, height: 36, background: '#f0f0f0', borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ccc' }}><FiUpload /></div>
                      )}
                    </td>
                    <td>{(a.created || a.updated) ? new Date(a.created || a.updated).toLocaleDateString('fr-FR') : '-'}</td>
                    <td><strong>{a.titre || '-'}</strong></td>
                    <td>{a.projet_libelle ? <span className="badge badge-blue">{a.projet_libelle}</span> : '-'}</td>
                    <td style={{ maxWidth: 250, fontSize: '0.82rem', color: '#777' }}>
                      {(a.article || '').replace(/<[^>]*>/g, '').substring(0, 80)}{(a.article || '').length > 80 ? '...' : ''}
                    </td>
                    <td>
                      <div className="actions">
                        <button className="btn-icon" style={{ color: '#1565C0' }} onClick={() => { setPreviewArticle(a); setShowPreview(true); }} title="Prévisualiser"><FiEye /></button>
                        <button className="btn-icon btn-warning" onClick={() => openEdit(a)}><FiEdit2 /></button>
                        <button className="btn-icon btn-danger" onClick={() => handleDelete(a.id)}><FiTrash2 /></button>
                      </div>
                    </td>
                  </tr>
                ))}
                {articles.length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: '2rem', color: '#999' }}>Aucun article</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal Edit/Create */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" style={{ maxWidth: '800px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingId ? 'Modifier l\'article' : 'Nouvel article'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}><FiX /></button>
            </div>
            <form onSubmit={handleSave}>
              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group" style={{ flex: 2 }}><label>Titre *</label><input className="form-input" value={form.titre} onChange={e => setForm({ ...form, titre: e.target.value })} required /></div>
                  <div className="form-group" style={{ flex: 1 }}><label>Projet</label>
                    <select className="form-input" value={form.projet_id} onChange={e => setForm({ ...form, projet_id: e.target.value })}>
                      <option value="">--</option>
                      {projets.map((p: any) => <option key={p.id} value={p.id}>{p.libelle}</option>)}
                    </select>
                  </div>
                </div>

                {/* Image upload */}
                <div className="form-group">
                  <label>Image principale</label>
                  <div className="file-upload-zone" onClick={() => document.getElementById('article-image-input')?.click()} style={{ padding: '12px 16px', minHeight: 'auto', cursor: 'pointer' }}>
                    <input id="article-image-input" type="file" accept="image/*" style={{ display: 'none' }}
                      onChange={handleImageChange} />
                    {imagePreview ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <img src={imagePreview} alt="Preview" style={{ width: 80, height: 56, objectFit: 'cover', borderRadius: 8 }} />
                        <div style={{ flex: 1 }}>
                          <strong style={{ fontSize: '0.85rem' }}>{imageFile ? imageFile.name : 'Image existante'}</strong>
                          {imageFile && <small style={{ display: 'block', color: '#888' }}>{(imageFile.size / 1024).toFixed(1)} Ko</small>}
                        </div>
                        <button type="button" className="btn-icon" onClick={e => { e.stopPropagation(); setImageFile(null); setImagePreview(''); }}><FiX /></button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#888' }}>
                        <FiUpload /> <span>Cliquer pour sélectionner une image</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="form-group">
                  <label>Contenu de l'article</label>
                  <div style={{ minHeight: '250px', marginBottom: '50px' }}>
                    <ReactQuill
                      theme="snow"
                      value={form.article}
                      onChange={(val: string) => setForm({ ...form, article: val })}
                      modules={quillModules}
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Enregistrement...' : 'Enregistrer'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && previewArticle && (
        <div className="modal-overlay" onClick={() => setShowPreview(false)}>
          <div className="modal" style={{ maxWidth: '700px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Prévisualisation</h3>
              <button className="modal-close" onClick={() => setShowPreview(false)}><FiX /></button>
            </div>
            <div className="modal-body">
              {previewArticle.imageprincipale && (
                <img src={articleImageUrl(previewArticle.imageprincipale)} alt="" style={{ width: '100%', height: 200, objectFit: 'cover', borderRadius: 10, marginBottom: 16 }} />
              )}
              <div style={{ fontSize: '0.82rem', color: '#1565C0', fontWeight: 600, marginBottom: 6 }}>
                {(previewArticle.created || previewArticle.updated) ? new Date(previewArticle.created || previewArticle.updated).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}
              </div>
              <h2 style={{ fontSize: '1.4rem', fontWeight: 800, color: '#1a1a2e', marginBottom: 12 }}>{previewArticle.titre}</h2>
              {previewArticle.projet_libelle && (
                <span className="badge badge-blue" style={{ marginBottom: 16, display: 'inline-block' }}>{previewArticle.projet_libelle}</span>
              )}
              <div style={{ fontSize: '0.95rem', color: '#444', lineHeight: 1.8, marginTop: 12 }}
                dangerouslySetInnerHTML={{ __html: previewArticle.article || '<em>Aucun contenu</em>' }} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Articles;
